import { Chip, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { FC } from 'react';
import React from 'react';
import { getInitials } from 'src/utils/get-initials';
import { CheckIcon, IconTask, TimeIcon } from '../dashboard-icon';

interface PatientListTableProps {
  items?: any;
}

const PreviewOfPatientTable: FC<PatientListTableProps> = (props) => {
  const { items = [] } = props;
  const statusStyleProps: any = {
    IN_PROGRESS: {
      marginLeft: '10px',
      color: '#6366F1',
      backgroundColor: '#EBEEFE',
    },
    NOT_STARTED: {
      marginLeft: '10px',
      color: '#6C737F',
      backgroundColor: 'rgba(17, 25, 39, 0.08)',
    },
    FINALIZED: {
      marginLeft: '10px',
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
    },
    SIGNED: {
      marginLeft: '10px',
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
    },
  };

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="center">Patient Name</TableCell>
            <TableCell>Task</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Diagnosis</TableCell>
            <TableCell>Bed Assignment</TableCell>
            {/* <TableCell>Note from previous therapy session</TableCell> */}
            <TableCell>Payer</TableCell>
            <TableCell>Codes</TableCell>
            <TableCell align="center">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                align="center"
              >
                <Typography
                  variant="h6"
                  sx={{ margin: '2.5rem auto' }}
                >
                  No Records Found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items?.map((patient: any) => {
              return (
                <TableRow
                  hover
                  key={patient?.uuid}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack
                      alignItems="center"
                      direction="row"
                      gap="1rem"
                    >
                      <Avatar
                        src={`data:image/png;base64,${patient?.profileImage}`}
                        sx={{
                          height: 42,
                          width: 42,
                        }}
                      >
                        {getInitials(patient?.name)}
                      </Avatar>
                      <Box>
                        {' '}
                        <Typography variant="subtitle2">{`${patient?.lastName || ''}, ${
                          patient?.firstName || ''
                        }`}</Typography>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="caption"
                        >
                          {patient?.patient_term}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      textTransform: 'capitalize',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        // alignItems: 'center',
                        gap: 1,
                        '@media print': { flexDirection: 'column' },
                      }}
                    >
                      <Box sx={{ display: 'flex' }}>
                        {patient?.taskNameWithId?.map((taskName: any) => (
                          <React.Fragment key={taskName?.key}>
                            <span title={taskName?.value}>
                              <IconTask iconType={taskName.key} />
                            </span>
                            {patient?.taskNameWithId?.length <= 2 ? (
                              <Typography
                                sx={{ '@media print': { display: 'none' }, ml: '2px' }}
                                variant="body2"
                              >
                                {taskName?.value}
                              </Typography>
                            ) : null}
                          </React.Fragment>
                        ))}
                      </Box>

                      <Box>
                        {patient?.therapyNames?.map((group: any) => {
                          return (
                            <Typography
                              sx={{
                                ml: 1,
                                color:
                                  group?.charAt(0) === 'G'
                                    ? '#06AED4'
                                    : group?.charAt(0) === 'C'
                                    ? '#F79009'
                                    : null,
                              }}
                              variant="caption"
                              noWrap
                              key={group}
                            >
                              {group}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TimeIcon />
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {patient?.taskMinutes}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {patient?.diagnosis || '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {patient?.bedAssignment || '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* <TableCell>
                   <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     <Typography
                       sx={{ marginLeft: '5px' }}
                       variant="body2"
                     >
                       {'NA'}
                     </Typography>
                   </Box>
                 </TableCell> */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {patient?.payer || '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {!!patient?.cptCodes?.length ? patient?.cptCodes?.join(', ') : '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="justify">
                    {patient?.status !== 'SIGNED' ? (
                      <Chip
                        label={
                          <Typography variant="overline">
                            {patient?.status
                              ?.split('_')
                              ?.map(
                                (item: string) =>
                                  item.slice(0, 1) + item.toLowerCase().slice(1, item.length)
                              )
                              .join(' ')}
                          </Typography>
                        }
                        variant="outlined"
                        // size="small"
                        sx={statusStyleProps[patient?.status]}
                      />
                    ) : (
                      <Chip
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CheckIcon />
                            <Typography variant="overline">Signed</Typography>
                          </Box>
                        }
                        variant="outlined"
                        // size="small"
                        sx={{
                          marginLeft: '10px',
                          color: '#107569',
                          backgroundColor: 'rgba(21, 183, 158, 0.12)',
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default PreviewOfPatientTable;
